/**
 * Utilidad para probar la conectividad con el backend
 * Este archivo ayuda a diagnosticar problemas de conectividad
 */

const API_BASE_URL = 'https://zona-virtual-cloud-backend.carlos-mdtz9.workers.dev';

export interface TestResult {
  endpoint: string;
  success: boolean;
  status?: number;
  error?: string;
  response?: any;
  timestamp: string;
}

export class ApiTester {
  private results: TestResult[] = [];

  /**
   * Probar un endpoint específico
   */
  async testEndpoint(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<TestResult> {
    const url = `${API_BASE_URL}${endpoint}`;
    const timestamp = new Date().toISOString();
    
    const result: TestResult = {
      endpoint,
      success: false,
      timestamp
    };

    try {
      console.log(`🔍 Probando endpoint: ${endpoint}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      result.status = response.status;
      result.success = response.ok;

      if (response.ok) {
        const data = await response.json();
        result.response = data;
        console.log(`✅ ${endpoint} - OK (${response.status})`, data);
      } else {
        const errorText = await response.text();
        result.error = `HTTP ${response.status}: ${errorText}`;
        console.error(`❌ ${endpoint} - Error (${response.status})`, errorText);
      }
    } catch (error) {
      result.error = error instanceof Error ? error.message : 'Error desconocido';
      console.error(`💥 ${endpoint} - Excepción`, error);
    }

    this.results.push(result);
    return result;
  }

  /**
   * Ejecutar todas las pruebas básicas
   */
  async runBasicTests(): Promise<TestResult[]> {
    console.log('🚀 Iniciando pruebas de conectividad con el backend...');
    
    const tests = [
      { endpoint: '/health', description: 'Health Check' },
      { endpoint: '/api/v1/auth/users/count', description: 'User Count (Public)' },
      { endpoint: '/api/v1/forum/categories', description: 'Forum Categories' },
      { endpoint: '/api/v1/forum/stats/forum-stats', description: 'Forum Stats' },
    ];

    for (const test of tests) {
      console.log(`\n📋 ${test.description}`);
      await this.testEndpoint(test.endpoint);
      
      // Pequeña pausa entre requests
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return this.results;
  }

  /**
   * Probar autenticación
   */
  async testAuthentication(): Promise<TestResult[]> {
    console.log('\n🔐 Probando autenticación...');
    
    // Probar login con credenciales de prueba
    const loginResult = await this.testEndpoint('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email: 'Carlos@Centurion.com',
        password: 'hardplay'
      })
    });

    if (loginResult.success && loginResult.response?.data?.token) {
      const token = loginResult.response.data.token;
      console.log('🎫 Token obtenido, probando endpoints protegidos...');
      
      // Probar endpoint protegido
      await this.testEndpoint('/api/v1/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    }

    return this.results;
  }

  /**
   * Obtener resumen de resultados
   */
  getSummary(): { total: number; successful: number; failed: number; results: TestResult[] } {
    const successful = this.results.filter(r => r.success).length;
    const failed = this.results.filter(r => !r.success).length;
    
    return {
      total: this.results.length,
      successful,
      failed,
      results: this.results
    };
  }

  /**
   * Limpiar resultados
   */
  clearResults(): void {
    this.results = [];
  }

  /**
   * Exportar resultados para debugging
   */
  exportResults(): string {
    return JSON.stringify(this.getSummary(), null, 2);
  }
}

// Instancia global para usar en la consola del navegador
export const apiTester = new ApiTester();

// Función de conveniencia para usar en la consola
export const testBackend = async () => {
  console.clear();
  console.log('🧪 La Costa Digital - PRUEBAS DE BACKEND');
  console.log('=====================================');
  
  await apiTester.runBasicTests();
  await apiTester.testAuthentication();
  
  const summary = apiTester.getSummary();
  console.log('\n📊 RESUMEN DE PRUEBAS');
  console.log('=====================');
  console.log(`Total: ${summary.total}`);
  console.log(`✅ Exitosas: ${summary.successful}`);
  console.log(`❌ Fallidas: ${summary.failed}`);
  
  if (summary.failed > 0) {
    console.log('\n❌ PRUEBAS FALLIDAS:');
    summary.results
      .filter(r => !r.success)
      .forEach(r => {
        console.log(`- ${r.endpoint}: ${r.error}`);
      });
  }
  
  return summary;
};

// Función para probar CORS específicamente
export const testCORS = async () => {
  console.log('🌐 Probando CORS...');
  
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'OPTIONS',
      headers: {
        'Origin': window.location.origin,
        'Access-Control-Request-Method': 'GET',
        'Access-Control-Request-Headers': 'Content-Type',
      }
    });
    
    console.log('CORS Preflight Response:', {
      status: response.status,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error en CORS preflight:', error);
    return false;
  }
};

